// FormulaCell — shared controlled formula-expression input primitive for inline grid edits.
// Domain-neutral (Component_Contract Tier 1). Theme tokens only.
//
// Props:
//   value      — current formula string (controlled)
//   onChange   — (nextValue: string) => void
//   placeholder?, onKeyDown?, title?
//
// Renders a text input with:
//   • an always-visible ƒ marker (prefix) — muted when the expression is empty, navy when
//     non-empty (color-blind safe: it's a glyph, not a color) — that DOUBLES as a formula
//     syntax help trigger (hover or click opens a cheat-sheet popover).
//   • a LIGHT malformed check (currently: unbalanced parentheses) surfaced inline as a
//     small ⚠ hint below the input. Shape + word carry meaning (color-blind safe).
// It does NOT evaluate or price the expression — validation is structural only.
//
// The help popover renders via createPortal(document.body) with FIXED positioning computed
// from the ƒ trigger's rect (flips upward when short on room below) — same pattern as
// CascadeSelect's popover, needed because FormulaCell renders inside DataGrid cells
// (overflow:hidden) which would otherwise clip it.

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CELL_INPUT_STYLE, CELL_FONT } from './cellStyles.js';
import { FORMULA } from '../icons.js';

// Light malformed check: returns a short message, or null if it looks OK.
// Intentionally shallow — this is a hint, not the pricing evaluator.
export function checkFormula(expr) {
  const s = (expr == null ? '' : String(expr)).trim();
  if (!s) return null;
  let depth = 0;
  for (const ch of s) {
    if (ch === '(') depth++;
    else if (ch === ')') { depth--; if (depth < 0) return 'unbalanced parentheses'; }
  }
  if (depth !== 0) return 'unbalanced parentheses';
  return null;
}

const helpPopStyle = {
  position: 'fixed',
  background: 'var(--th-row-even)',
  border: 'var(--tg-filter-border)',
  borderRadius: 3,
  zIndex: 300,
  boxShadow: '0 2px 8px rgba(0,0,0,0.14)',
  maxWidth: 360,
  padding: 8,
};

// Compute the fixed-position style for the help popover from the ƒ trigger's rect.
// Opens downward by default; flips upward when there's more room above.
function popPosition(rect) {
  const below = window.innerHeight - rect.bottom - 8;
  const above = rect.top - 8;
  const openUp = below < 200 && above > below;
  return {
    left: rect.left,
    ...(openUp
      ? { bottom: window.innerHeight - rect.top + 2 }
      : { top: rect.bottom + 2 }),
  };
}

const rowStyle = { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8, alignItems: 'baseline', padding: '2px 0' };
const syntaxStyle = { fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-small)', color: 'var(--th-navy)', whiteSpace: 'nowrap' };
const meaningStyle = { fontSize: 'var(--fs-small)', color: 'var(--th-text-body)' };

function FormulaHelpContent() {
  return (
    <div onClick={e => e.stopPropagation()}>
      <div style={{ fontSize: 'var(--fs-small)', fontWeight: 700, color: 'var(--th-text-body)', marginBottom: 4 }}>
        Formula syntax
      </div>
      <div style={rowStyle}>
        <span style={syntaxStyle}>+ - * /  ^  ( )</span>
        <span style={meaningStyle}>arithmetic, power, grouping</span>
      </div>
      <div style={rowStyle}>
        <span style={syntaxStyle}>{'< <= > >= == !='}</span>
        <span style={meaningStyle}>comparisons (give 1 or 0)</span>
      </div>
      <div style={rowStyle}>
        <span style={syntaxStyle}>{'&& ||'}</span>
        <span style={meaningStyle}>and / or</span>
      </div>
      <div style={rowStyle}>
        <span style={syntaxStyle}>cond ? a : b</span>
        <span style={meaningStyle}>if-then-else, nestable: <span style={syntaxStyle}>Size&lt;2 ? 1 : Size&lt;5 ? 2 : 3</span></span>
      </div>
      <div style={rowStyle}>
        <span style={syntaxStyle}>min max round floor ceil sqrt abs</span>
        <span style={meaningStyle}>functions, e.g. <span style={syntaxStyle}>ceil(Length/6)</span></span>
      </div>
      <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-text-secondary)', marginTop: 6 }}>
        Parameter names are case-sensitive · anything invalid shows ⚠ · see FORMULAS.md
      </div>
    </div>
  );
}

export default function FormulaCell({ value, onChange, placeholder, onKeyDown, title }) {
  const str = value == null ? '' : String(value);
  const nonEmpty = str.trim().length > 0;
  const warn = checkFormula(str);

  const [helpOpen, setHelpOpen] = useState(false);
  const [helpPos, setHelpPos] = useState(null);
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const closeTimerRef = useRef(null);

  const openHelp = useCallback(() => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
    if (triggerRef.current) setHelpPos(popPosition(triggerRef.current.getBoundingClientRect()));
    setHelpOpen(true);
  }, []);

  const closeHelp = useCallback(() => { setHelpOpen(false); setHelpPos(null); }, []);

  const scheduleClose = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => { closeHelp(); closeTimerRef.current = null; }, 150);
  }, [closeHelp]);

  const toggleHelp = useCallback((e) => {
    e.stopPropagation();
    if (helpOpen) closeHelp(); else openHelp();
  }, [helpOpen, openHelp, closeHelp]);

  // Escape / click-outside dismissal — the popover lives in a body portal, so check both refs.
  useEffect(() => {
    if (!helpOpen) return undefined;
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      closeHelp();
    };
    const onKey = (e) => { if (e.key === 'Escape') closeHelp(); };
    const onScroll = (e) => { if (popRef.current && popRef.current.contains(e.target)) return; closeHelp(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', closeHelp);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', closeHelp);
    };
  }, [helpOpen, closeHelp]);

  useEffect(() => () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }} onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, width: '100%' }}>
        <span
          ref={triggerRef}
          role="button"
          tabIndex={0}
          aria-label="Formula syntax help"
          onMouseEnter={openHelp}
          onMouseLeave={scheduleClose}
          onClick={toggleHelp}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleHelp(e); }}
          style={{
            fontFamily: 'var(--ff-mono)', fontStyle: 'italic', fontWeight: 700,
            fontSize: 'var(--fs-body)', lineHeight: 1, flexShrink: 0, cursor: 'help',
            color: nonEmpty ? 'var(--th-navy)' : 'var(--th-text-disabled)',
          }}
        >{FORMULA}</span>
        <input
          type="text"
          value={str}
          placeholder={placeholder}
          title={title}
          onChange={e => onChange(e.target.value)}
          onClick={e => e.stopPropagation()}
          onKeyDown={onKeyDown}
          style={{ ...CELL_INPUT_STYLE, fontFamily: nonEmpty ? 'var(--ff-mono)' : CELL_FONT.fontFamily }}
        />
      </div>
      {warn && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 'var(--fs-micro)', color: 'var(--th-warn-accent)' }}>
          <span aria-hidden="true">⚠</span>
          <span>{warn}</span>
        </span>
      )}
      {helpOpen && helpPos && createPortal(
        <div
          ref={popRef}
          style={{ ...helpPopStyle, ...helpPos }}
          onMouseEnter={() => { if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; } }}
          onMouseLeave={scheduleClose}
          onClick={e => e.stopPropagation()}
        >
          <FormulaHelpContent />
        </div>,
        document.body
      )}
    </div>
  );
}
